import { Loader2, Sparkles, Users } from 'lucide-react';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { useWikiManifest, useWikiPage, type WikiManifestPage } from '../api/wiki.js';
import { MarkdownRenderer } from '../components/markdown-renderer.js';
import { PageHeader } from '../components/page-header.js';

import { WikiSidebar } from './wiki-sidebar.js';

const SIDEBAR_DEFAULT = 260;
const SIDEBAR_MIN = 160;
const SIDEBAR_MAX = 480;

export function WikiPage(): JSX.Element {
  const { name } = useParams<{ name: string }>();
  const [params, setParams] = useSearchParams();
  const selectedSlug = params.get('page');

  const manifest = useWikiManifest(name);
  const pages: readonly WikiManifestPage[] = manifest.data?.pages ?? [];
  const activeSlug = useMemo(() => {
    if (selectedSlug && pages.some((p) => p.slug === selectedSlug)) return selectedSlug;
    return pages[0]?.slug;
  }, [selectedSlug, pages]);

  const page = useWikiPage(name, activeSlug);
  const activeMeta = pages.find((p) => p.slug === activeSlug);

  const [sidebarWidth, setSidebarWidth] = useState<number>(SIDEBAR_DEFAULT);
  const dragging = useRef<boolean>(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onMove = (e: PointerEvent): void => {
      if (!dragging.current) return;
      const rect = containerRef.current?.getBoundingClientRect();
      const left = rect?.left ?? 0;
      const next = Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, e.clientX - left));
      setSidebarWidth(next);
    };
    const onUp = (): void => {
      if (!dragging.current) return;
      dragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, []);

  const startResize = (e: ReactPointerEvent<HTMLDivElement>): void => {
    e.preventDefault();
    dragging.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  if (!name) return <p className="p-8 text-text-muted">Missing repo name.</p>;
  if (manifest.isLoading) {
    return (
      <p className="flex items-center gap-2 p-8 font-mono text-xs text-text-secondary">
        <Loader2 className="h-3.5 w-3.5 animate-spin text-accent" />
        Loading wiki…
      </p>
    );
  }
  if (manifest.error) {
    return (
      <p className="m-8 rounded-md border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-300">
        Failed to load wiki: {String(manifest.error)}
      </p>
    );
  }
  if (pages.length === 0) {
    return (
      <section className="mx-auto flex w-full max-w-3xl flex-col gap-3 px-6 py-8">
        <PageHeader eyebrow={name} title="Wiki" />
        <p className="rounded-2xl border border-dashed border-border-subtle bg-surface/40 p-6 text-sm text-text-muted">
          No wiki pages have been generated for{' '}
          <strong className="text-text-primary">{name}</strong> yet. Trigger a wiki regeneration job
          to create one.
        </p>
      </section>
    );
  }

  return (
    <section ref={containerRef} className="mx-auto flex w-full max-w-7xl flex-row gap-0 px-6 py-8">
      <div style={{ width: sidebarWidth }} className="flex-none">
        <WikiSidebar
          pages={pages}
          activeSlug={activeSlug}
          onSelect={(slug) => setParams({ page: slug })}
        />
      </div>
      <div
        onPointerDown={startResize}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize sidebar"
        className="mx-2 w-1 flex-none cursor-col-resize rounded-full bg-border-subtle transition-colors hover:bg-accent/50"
      />
      <article className="min-w-0 flex-1 overflow-hidden rounded-2xl border border-border-subtle bg-surface/60 p-6 backdrop-blur">
        <header className="flex flex-wrap items-center gap-3">
          <h1 className="text-xl font-semibold text-text-primary">
            {activeMeta?.title ?? activeSlug}
          </h1>
          {activeMeta && <SourceBadge source={activeMeta.source} />}
          {typeof activeMeta?.memberCount === 'number' && (
            <span className="inline-flex items-center gap-1 font-mono text-[11px] text-text-muted">
              <Users className="h-3 w-3" />
              {activeMeta.memberCount} members
            </span>
          )}
        </header>
        {page.isLoading && (
          <p className="mt-4 flex items-center gap-2 font-mono text-xs text-text-secondary">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-accent" />
            Loading page…
          </p>
        )}
        {page.error && (
          <p className="mt-4 rounded-md border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-300">
            Failed to load page: {String(page.error)}
          </p>
        )}
        {page.data && (
          <div className="mt-4">
            <MarkdownRenderer content={page.data} />
          </div>
        )}
      </article>
    </section>
  );
}

interface SourceBadgeProps {
  readonly source: WikiManifestPage['source'];
}

function SourceBadge({ source }: SourceBadgeProps): JSX.Element | null {
  if (!source) return null;
  const isAi = source === 'connected-components';
  const isPath = source === 'path-fallback';
  if (!isAi && !isPath) return null;
  const label = isAi ? 'AI-Grouped' : 'Path-Grouped';
  const styles = isAi
    ? 'border-emerald-500/30 bg-emerald-500/15 text-emerald-300'
    : 'border-amber-500/30 bg-amber-500/15 text-amber-300';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${styles}`}
      aria-label={`Wiki source: ${label}`}
    >
      <Sparkles className="h-3 w-3" />
      {label}
    </span>
  );
}
